import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, ChevronRight, Search } from "lucide-react";

const ROLES = ["Batter", "Bowler", "All-rounder", "Wicket-keeper"];
const HANDS = ["Right-hand", "Left-hand"];
const BOWLING = ["Right-arm fast", "Right-arm medium", "Off-spin", "Leg-spin", "Left-arm orthodox", "Left-arm fast", "None"];
const DIVISIONS = ["A Division", "B Division", "C Division", "D Division", "E Division", "School/College"];
const AGE_GROUPS = ["Under-14", "Under-16", "Under-19", "Under-23", "Open/Senior"];
const METRICS = ["runs", "wickets", "average", "strike_rate", "economy", "matches"];

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${active ? "border-brand bg-brand text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      {children}
    </button>
  );
}

export default function Onboarding() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    role_type: "", batting_hand: "", bowling_type: "", clubs: [], age_group: "", division: "",
    tournaments: [], positions: [], season_goals: [], bio: "",
    privacy: { is_public: false, public_fields: { batting: true, bowling: true, bio: true } },
  });
  const [clubInput, setClubInput] = useState("");
  const [tourInput, setTourInput] = useState("");
  const [goal, setGoal] = useState({ title: "", metric: "runs", target: "" });
  const [coachQuery, setCoachQuery] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [taggedCoach, setTaggedCoach] = useState(null);

  useEffect(() => {
    if (loading || user === null) return;
    if (!user) navigate("/login");
    else if (user.role === "coach") navigate("/coach");
    else if (user.onboarding_complete) navigate("/app/dashboard");
  }, [user, loading, navigate]);

  const strengthFields = ["role_type", "batting_hand", "bowling_type", "clubs", "age_group", "division", "tournaments", "positions", "season_goals", "bio"];
  const filled = strengthFields.filter((f) => { const v = data[f]; return Array.isArray(v) ? v.length : v; }).length;
  const strength = Math.min(100, Math.round(15 + (100 - 15) * (filled / strengthFields.length)));

  const searchCoaches = async (q) => {
    setCoachQuery(q);
    if (q.length < 2) { setCoaches([]); return; }
    const { data } = await api.get(`/coaches/search?q=${encodeURIComponent(q)}`);
    setCoaches(data);
  };

  const tagCoach = async (c) => {
    try {
      await api.post("/coach-links", { coach_id: c.id });
      setTaggedCoach(c);
      toast.success(`Link request sent to ${c.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const finish = async (skip = false) => {
    try {
      const payload = skip ? { onboarding_complete: true } : { ...data, onboarding_complete: true };
      await api.put("/profile", payload);
      await refresh();
      toast.success(skip ? "You can complete your profile anytime" : "Profile set up!");
      navigate("/app/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const steps = [
    {
      title: "Your cricket identity",
      body: (
        <div className="space-y-6">
          <div><Label className="mb-2 block">Primary role</Label><div className="flex flex-wrap gap-2">{ROLES.map((r) => <Pill key={r} testid={`role-${r}`} active={data.role_type === r} onClick={() => setData({ ...data, role_type: r })}>{r}</Pill>)}</div></div>
          <div><Label className="mb-2 block">Batting hand</Label><div className="flex flex-wrap gap-2">{HANDS.map((r) => <Pill key={r} testid={`hand-${r}`} active={data.batting_hand === r} onClick={() => setData({ ...data, batting_hand: r })}>{r}</Pill>)}</div></div>
          <div><Label className="mb-2 block">Bowling type</Label><div className="flex flex-wrap gap-2">{BOWLING.map((r) => <Pill key={r} testid={`bowl-${r}`} active={data.bowling_type === r} onClick={() => setData({ ...data, bowling_type: r })}>{r}</Pill>)}</div></div>
        </div>
      ),
    },
    {
      title: "Clubs & level",
      body: (
        <div className="space-y-6">
          <div>
            <Label className="mb-2 block">MCA Club(s)</Label>
            <div className="flex gap-2">
              <Input value={clubInput} onChange={(e) => setClubInput(e.target.value)} placeholder="e.g. Shivaji Park Gymkhana" data-testid="club-input"
                onKeyDown={(e) => { if (e.key === "Enter" && clubInput.trim()) { e.preventDefault(); setData({ ...data, clubs: [...data.clubs, clubInput.trim()] }); setClubInput(""); } }} />
              <Button type="button" variant="outline" onClick={() => { if (clubInput.trim()) { setData({ ...data, clubs: [...data.clubs, clubInput.trim()] }); setClubInput(""); } }}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">{data.clubs.map((c, i) => <span key={i} className="px-3 py-1 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2">{c}<button onClick={() => setData({ ...data, clubs: data.clubs.filter((_, j) => j !== i) })}>×</button></span>)}</div>
          </div>
          <div><Label className="mb-2 block">Age group</Label><div className="flex flex-wrap gap-2">{AGE_GROUPS.map((r) => <Pill key={r} active={data.age_group === r} onClick={() => setData({ ...data, age_group: r })}>{r}</Pill>)}</div></div>
          <div><Label className="mb-2 block">Division</Label><div className="flex flex-wrap gap-2">{DIVISIONS.map((r) => <Pill key={r} active={data.division === r} onClick={() => setData({ ...data, division: r })}>{r}</Pill>)}</div></div>
        </div>
      ),
    },
    {
      title: "Tournaments & about you",
      body: (
        <div className="space-y-6">
          <div>
            <Label className="mb-2 block">Tournaments you play</Label>
            <div className="flex gap-2">
              <Input value={tourInput} onChange={(e) => setTourInput(e.target.value)} placeholder="e.g. Kanga League" data-testid="tour-input"
                onKeyDown={(e) => { if (e.key === "Enter" && tourInput.trim()) { e.preventDefault(); setData({ ...data, tournaments: [...data.tournaments, tourInput.trim()] }); setTourInput(""); } }} />
              <Button type="button" variant="outline" onClick={() => { if (tourInput.trim()) { setData({ ...data, tournaments: [...data.tournaments, tourInput.trim()] }); setTourInput(""); } }}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">{data.tournaments.map((c, i) => <span key={i} className="px-3 py-1 rounded-full bg-brand text-white text-sm flex items-center gap-2">{c}<button onClick={() => setData({ ...data, tournaments: data.tournaments.filter((_, j) => j !== i) })}>×</button></span>)}</div>
          </div>
          <div><Label className="mb-1 block">Short bio</Label><textarea className="w-full rounded-lg border border-slate-200 p-3 text-sm" rows={3} value={data.bio} onChange={(e) => setData({ ...data, bio: e.target.value })} placeholder="Top-order batter who loves the pull shot…" data-testid="bio-input" /></div>
        </div>
      ),
    },
    {
      title: "Season goals",
      body: (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-4 gap-2 items-end">
            <div className="sm:col-span-2"><Label className="mb-1 block">Goal</Label><Input value={goal.title} onChange={(e) => setGoal({ ...goal, title: e.target.value })} placeholder="Score 500 runs" data-testid="goal-title" /></div>
            <div><Label className="mb-1 block">Metric</Label>
              <select className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={goal.metric} onChange={(e) => setGoal({ ...goal, metric: e.target.value })} data-testid="goal-metric">
                {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><Label className="mb-1 block">Target</Label><Input type="number" value={goal.target} onChange={(e) => setGoal({ ...goal, target: e.target.value })} data-testid="goal-target" /></div>
          </div>
          <Button type="button" variant="outline" data-testid="add-goal" onClick={() => { if (goal.title && goal.target) { setData({ ...data, season_goals: [...data.season_goals, { ...goal, target: Number(goal.target) }] }); setGoal({ title: "", metric: "runs", target: "" }); } }}>Add goal</Button>
          <div className="space-y-2">{data.season_goals.map((g, i) => <div key={i} className="flex items-center justify-between bg-slate-100 rounded-lg p-3 text-sm"><span className="font-semibold">{g.title} <span className="text-slate-500">({g.metric} ≥ {g.target})</span></span><button onClick={() => setData({ ...data, season_goals: data.season_goals.filter((_, j) => j !== i) })}>×</button></div>)}</div>
        </div>
      ),
    },
    {
      title: "Link a coach (optional)",
      body: (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Tag your coach so they can follow your progress. They'll approve the request.</p>
          {taggedCoach ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3"><Check className="text-green-600" /><span>Request sent to <b>{taggedCoach.name}</b> — pending approval.</span></div>
          ) : (
            <>
              <div className="relative"><Search className="absolute left-3 top-2.5 text-slate-400" size={18} /><Input className="pl-10" value={coachQuery} onChange={(e) => searchCoaches(e.target.value)} placeholder="Search coach by name or email" data-testid="coach-search" /></div>
              <div className="space-y-2">{coaches.map((c) => <button key={c.id} onClick={() => tagCoach(c)} data-testid={`tag-coach-${c.id}`} className="w-full text-left bg-slate-100 hover:bg-slate-200 rounded-lg p-3 flex justify-between items-center"><span><b>{c.name}</b> <span className="text-slate-500 text-sm">{c.email}</span></span><ChevronRight size={16} /></button>)}</div>
            </>
          )}
        </div>
      ),
    },
  ];

  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-ink text-white"><div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between"><Logo light /><button onClick={() => finish(true)} className="text-slate-300 text-sm hover:text-white" data-testid="skip-onboarding">Skip for now</button></div></header>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="onboarding-wizard-container">
        {/* strength meter */}
        <div className="ct-card p-4 mb-6">
          <div className="flex justify-between items-center mb-2"><span className="text-xs font-bold uppercase tracking-widest text-slate-500">Profile strength</span><span className="stat-num text-brand text-xl" data-testid="profile-strength-meter">{strength}%</span></div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-brand transition-all duration-500" style={{ width: `${strength}%` }} /></div>
        </div>

        <div className="flex items-center gap-2 mb-6" data-testid="onboarding-step-indicator">
          {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-slate-200"}`} />)}
        </div>

        <div className="ct-card p-6 sm:p-8 fade-up" key={step}>
          <p className="text-xs font-bold uppercase tracking-widest text-brand mb-1">Step {step + 1} of {steps.length}</p>
          <h1 className="font-head font-bold text-3xl mb-6">{steps[step].title}</h1>
          {steps[step].body}
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back-button">Back</Button>
          {isLast ? (
            <Button className="bg-brand hover:bg-brand-dark px-8" onClick={() => finish(false)} data-testid="onboarding-finish-button">Finish setup</Button>
          ) : (
            <Button className="bg-brand hover:bg-brand-dark px-8" onClick={() => setStep(step + 1)} data-testid="onboarding-next-button">Continue</Button>
          )}
        </div>
      </div>
    </div>
  );
}
